/** @module @liquidefy/code-editor/editor/editor-command @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  CommandData,
  CommandRegistry,
  CommandId,
  KeybindingData,
  KeybindingId,
  KeybindingResolveResult,
  ChordKeybinding,
  WhenClause,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  asCommandId,
  asKeybindingId,
  generateId,
} from '../code-editor.js';

// ═══ §2 COMMAND REGISTRY — register, unregister, query, search ═══

/**
 * Creates a new command registry, optionally pre-populated with commands.
 */
export function createCommandRegistry(
  commands?: CommandData[],
): EditorResult<CommandRegistry> {
  const map = new Map<CommandId, CommandData>();
  if (commands) {
    for (const cmd of commands) {
      map.set(cmd.id, cmd);
    }
  }
  return ok({ commands: map }, 'REGISTRY_CREATED', `Registry created with ${map.size} commands`);
}

/**
 * Registers a command in the registry.
 */
export function registerCommand(
  registry: CommandRegistry,
  command: CommandData,
): EditorResult<CommandRegistry> {
  const newMap = new Map(registry.commands);
  newMap.set(command.id, command);
  return ok({ commands: newMap }, 'COMMAND_REGISTERED', `Command ${command.id} registered`);
}

/**
 * Unregisters a command from the registry.
 */
export function unregisterCommand(
  registry: CommandRegistry,
  id: CommandId,
): EditorResult<CommandRegistry> {
  if (!registry.commands.has(id)) {
    return err('NOT_FOUND', `Command ${id} not found`);
  }
  const newMap = new Map(registry.commands);
  newMap.delete(id);
  return ok({ commands: newMap }, 'COMMAND_UNREGISTERED', `Command ${id} unregistered`);
}

/**
 * Retrieves a command by ID.
 */
export function getCommand(
  registry: CommandRegistry,
  id: CommandId,
): CommandData | null {
  return registry.commands.get(id) ?? null;
}

/**
 * Checks whether a command exists in the registry.
 */
export function hasCommand(registry: CommandRegistry, id: CommandId): boolean {
  return registry.commands.has(id);
}

/**
 * Lists all commands, optionally filtered by category.
 */
export function listCommands(
  registry: CommandRegistry,
  category?: string,
): CommandData[] {
  const all = Array.from(registry.commands.values());
  if (!category) return all;
  return all.filter((cmd) => cmd.category === category);
}

/**
 * Fuzzy-searches commands by query against id, title, and category.
 */
export function searchCommands(
  registry: CommandRegistry,
  query: string,
): CommandData[] {
  if (query.length === 0) return Array.from(registry.commands.values());

  const lower = query.toLowerCase();
  const results: { cmd: CommandData; score: number }[] = [];

  for (const cmd of registry.commands.values()) {
    let score = 0;

    const idLower = (cmd.id as string).toLowerCase();
    const titleLower = cmd.title.toLowerCase();
    const catLower = (cmd.category ?? '').toLowerCase();

    // Exact match in title
    if (titleLower === lower) score += 100;
    else if (titleLower.startsWith(lower)) score += 80;
    else if (titleLower.includes(lower)) score += 60;

    // Match in ID
    if (idLower.includes(lower)) score += 40;

    // Match in category
    if (catLower.includes(lower)) score += 20;

    // Fuzzy: check if all query chars appear in order
    if (score === 0) {
      const fuzzyScore = fuzzyMatch(lower, titleLower + ' ' + idLower);
      if (fuzzyScore > 0) score += fuzzyScore;
    }

    if (score > 0) {
      results.push({ cmd, score });
    }
  }

  results.sort((a, b) => b.score - a.score);
  return results.map((r) => r.cmd);
}

/**
 * Returns the total number of registered commands.
 */
export function commandCount(registry: CommandRegistry): number {
  return registry.commands.size;
}

// ═══ §3 KEYBINDING RESOLUTION — resolve key combos, conflicts, CRUD ═══

/**
 * Resolves a key combination against the keybinding list, evaluating when-clauses.
 */
export function resolveKeybinding(
  keybindings: KeybindingData[],
  keyCombo: string,
  context: Record<string, unknown>,
): KeybindingResolveResult | null {
  const normalized = normalizeKeyCombo(parseKeyCombo(keyCombo));

  // Find all matching keybindings
  const matches = keybindings.filter((kb) => {
    const kbNormalized = normalizeKeyCombo(parseKeyCombo(kb.key));
    return keyComboToString(kbNormalized) === keyComboToString(normalized);
  });

  if (matches.length === 0) return null;

  // Evaluate when-clauses, most specific wins
  for (const match of matches) {
    if (!match.when || evaluateWhenClause(match.when, context)) {
      return {
        command: match.command,
        args: match.args,
        when: match.when,
      };
    }
  }

  return null;
}

/**
 * Finds conflicting keybindings (same key, overlapping when-clauses).
 */
export function findConflicts(
  keybindings: KeybindingData[],
): Array<[KeybindingData, KeybindingData]> {
  const conflicts: Array<[KeybindingData, KeybindingData]> = [];

  for (let i = 0; i < keybindings.length; i++) {
    for (let j = i + 1; j < keybindings.length; j++) {
      const a = keybindings[i]!;
      const b = keybindings[j]!;

      const aNorm = keyComboToString(normalizeKeyCombo(parseKeyCombo(a.key)));
      const bNorm = keyComboToString(normalizeKeyCombo(parseKeyCombo(b.key)));

      if (aNorm === bNorm) {
        // Same key combo — conflict if when-clauses overlap
        if (!a.when && !b.when) {
          conflicts.push([a, b]);
        } else if (!a.when || !b.when) {
          // One has no when-clause, always overlaps
          conflicts.push([a, b]);
        }
        // If both have when-clauses, we'd need deep analysis — flag as potential conflict
      }
    }
  }

  return conflicts;
}

/**
 * Adds a keybinding to the list.
 */
export function addKeybinding(
  keybindings: KeybindingData[],
  binding: KeybindingData,
): KeybindingData[] {
  return [...keybindings, binding];
}

/**
 * Removes a keybinding by ID.
 */
export function removeKeybinding(
  keybindings: KeybindingData[],
  id: KeybindingId,
): KeybindingData[] {
  return keybindings.filter((kb) => kb.id !== id);
}

/**
 * Updates the key combination for an existing keybinding.
 */
export function updateKeybinding(
  keybindings: KeybindingData[],
  id: KeybindingId,
  newKey: string,
): EditorResult<KeybindingData[]> {
  const index = keybindings.findIndex((kb) => kb.id === id);
  if (index === -1) return err('NOT_FOUND', `Keybinding ${id} not found`);

  const result = [...keybindings];
  result[index] = { ...keybindings[index]!, key: newKey } as KeybindingData;
  return ok(result, 'KEYBINDING_UPDATED', `Keybinding ${id} updated to ${newKey}`);
}

/**
 * Resets a keybinding to its default key by looking up the command in the defaults list.
 */
export function resetKeybinding(
  keybindings: KeybindingData[],
  id: KeybindingId,
  defaults: KeybindingData[],
): EditorResult<KeybindingData[]> {
  const index = keybindings.findIndex((kb) => kb.id === id);
  if (index === -1) return err('NOT_FOUND', `Keybinding ${id} not found`);

  const current = keybindings[index]!;
  const defaultBinding = defaults.find((d) => d.command === current.command);
  if (!defaultBinding) {
    return err('NO_DEFAULT', `No default keybinding for command ${current.command}`);
  }

  const result = [...keybindings];
  result[index] = { ...current, key: defaultBinding.key } as KeybindingData;
  return ok(result, 'KEYBINDING_RESET', `Keybinding ${id} reset to default`);
}

/**
 * Returns all keybindings for a specific command.
 */
export function getKeybindingsForCommand(
  keybindings: KeybindingData[],
  commandId: CommandId,
): KeybindingData[] {
  return keybindings.filter((kb) => kb.command === commandId);
}

/**
 * Formats a keybinding for display, platform-aware.
 */
export function formatKeybinding(
  key: string,
  platform: 'mac' | 'windows' | 'linux',
): string {
  const combo = parseKeyCombo(key);

  const parts: string[] = [];

  if (combo.ctrl) {
    parts.push(platform === 'mac' ? '\u2303' : 'Ctrl');
  }
  if (combo.shift) {
    parts.push(platform === 'mac' ? '\u21E7' : 'Shift');
  }
  if (combo.alt) {
    parts.push(platform === 'mac' ? '\u2325' : 'Alt');
  }
  if (combo.meta) {
    parts.push(platform === 'mac' ? '\u2318' : 'Win');
  }

  // Format the key itself
  const keyDisplay = formatKeyName(combo.key);
  parts.push(keyDisplay);

  return platform === 'mac' ? parts.join('') : parts.join('+');
}

// ═══ §4 KEY PARSING — parse, normalize, compare key combos ═══

/**
 * Parsed key combination.
 */
interface KeyCombo {
  readonly ctrl: boolean;
  readonly shift: boolean;
  readonly alt: boolean;
  readonly meta: boolean;
  readonly key: string;
}

/**
 * Parses a key combination string (e.g., "ctrl+shift+p") into its components.
 */
export function parseKeyCombo(key: string): KeyCombo {
  const parts = key.toLowerCase().split('+').map((p) => p.trim());

  let ctrl = false;
  let shift = false;
  let alt = false;
  let meta = false;
  let mainKey = '';

  for (const part of parts) {
    switch (part) {
      case 'ctrl':
      case 'control':
        ctrl = true;
        break;
      case 'shift':
        shift = true;
        break;
      case 'alt':
      case 'option':
        alt = true;
        break;
      case 'meta':
      case 'cmd':
      case 'command':
      case 'win':
      case 'super':
        meta = true;
        break;
      default:
        mainKey = part;
    }
  }

  return { ctrl, shift, alt, meta, key: mainKey };
}

/**
 * Normalizes a key combo to a canonical form (sorted modifiers + key).
 */
export function normalizeKeyCombo(combo: KeyCombo): KeyCombo {
  return {
    ctrl: combo.ctrl,
    shift: combo.shift,
    alt: combo.alt,
    meta: combo.meta,
    key: combo.key.toLowerCase(),
  };
}

/**
 * Converts a key combo back to a string representation.
 */
export function keyComboToString(combo: KeyCombo): string {
  const parts: string[] = [];
  if (combo.ctrl) parts.push('ctrl');
  if (combo.shift) parts.push('shift');
  if (combo.alt) parts.push('alt');
  if (combo.meta) parts.push('meta');
  parts.push(combo.key);
  return parts.join('+');
}

/**
 * Returns true if the key is a modifier key (not a main key).
 */
export function isModifierKey(key: string): boolean {
  return MODIFIER_KEYS.includes(key.toLowerCase());
}

/**
 * List of modifier key names.
 */
export const MODIFIER_KEYS = [
  'ctrl', 'control', 'shift', 'alt', 'option',
  'meta', 'cmd', 'command', 'win', 'super',
];

/**
 * Returns true if the key string represents a chord keybinding (two parts separated by space).
 */
export function isChordKeybinding(key: string): boolean {
  return key.includes(' ') && key.split(' ').length === 2;
}

/**
 * Parses a chord keybinding ("ctrl+k ctrl+c") into its two parts.
 */
export function parseChord(key: string): EditorResult<ChordKeybinding> {
  const parts = key.split(' ').map((p) => p.trim()).filter((p) => p.length > 0);
  if (parts.length !== 2) {
    return err('INVALID_CHORD', 'Chord must have exactly two parts separated by space');
  }
  return ok(
    { firstPart: parts[0]!, chordPart: parts[1]! },
    'CHORD_PARSED',
    'Chord keybinding parsed',
  );
}

/**
 * Compares two key combos for equality.
 */
export function keyComboEquals(a: KeyCombo, b: KeyCombo): boolean {
  return (
    a.ctrl === b.ctrl &&
    a.shift === b.shift &&
    a.alt === b.alt &&
    a.meta === b.meta &&
    a.key.toLowerCase() === b.key.toLowerCase()
  );
}

// ═══ §5 WHEN CLAUSE EVALUATION — parse and evaluate contextual predicates ═══

/**
 * AST node for when-clause expressions.
 */
type WhenClauseNode =
  | { readonly type: 'and'; readonly left: WhenClauseNode; readonly right: WhenClauseNode }
  | { readonly type: 'or'; readonly left: WhenClauseNode; readonly right: WhenClauseNode }
  | { readonly type: 'not'; readonly operand: WhenClauseNode }
  | { readonly type: 'equals'; readonly key: string; readonly value: string }
  | { readonly type: 'notEquals'; readonly key: string; readonly value: string }
  | { readonly type: 'key'; readonly key: string }
  | { readonly type: 'regex'; readonly key: string; readonly pattern: string };

/**
 * Evaluates a when-clause string against a context dictionary.
 */
export function evaluateWhenClause(
  clause: WhenClause,
  context: Record<string, unknown>,
): boolean {
  if (!clause || clause.length === 0) return true;

  const ast = parseWhenClause(clause);
  return evaluateNode(ast, context);
}

/**
 * Parses a when-clause string into an AST.
 * Supports: &&, ||, !, ==, !=, =~, and bare keys (truthy check).
 */
export function parseWhenClause(clause: string): WhenClauseNode {
  const trimmed = clause.trim();

  // Handle OR (lowest precedence)
  const orParts = splitByOperator(trimmed, '||');
  if (orParts.length > 1) {
    let result = parseWhenClause(orParts[0]!);
    for (let i = 1; i < orParts.length; i++) {
      result = { type: 'or', left: result, right: parseWhenClause(orParts[i]!) };
    }
    return result;
  }

  // Handle AND
  const andParts = splitByOperator(trimmed, '&&');
  if (andParts.length > 1) {
    let result = parseWhenClause(andParts[0]!);
    for (let i = 1; i < andParts.length; i++) {
      result = { type: 'and', left: result, right: parseWhenClause(andParts[i]!) };
    }
    return result;
  }

  // Handle NOT
  if (trimmed.startsWith('!')) {
    return { type: 'not', operand: parseWhenClause(trimmed.substring(1)) };
  }

  // Handle regex match: key =~ /pattern/
  const regexMatch = trimmed.match(/^(\w[\w.]*)\s*=~\s*\/(.+)\/$/);
  if (regexMatch) {
    return { type: 'regex', key: regexMatch[1]!, pattern: regexMatch[2]! };
  }

  // Handle not-equals: key != value
  const neqIdx = trimmed.indexOf('!=');
  if (neqIdx !== -1) {
    const key = trimmed.substring(0, neqIdx).trim();
    const value = trimmed.substring(neqIdx + 2).trim().replace(/^'|'$/g, '').replace(/^"|"$/g, '');
    return { type: 'notEquals', key, value };
  }

  // Handle equals: key == value
  const eqIdx = trimmed.indexOf('==');
  if (eqIdx !== -1) {
    const key = trimmed.substring(0, eqIdx).trim();
    const value = trimmed.substring(eqIdx + 2).trim().replace(/^'|'$/g, '').replace(/^"|"$/g, '');
    return { type: 'equals', key, value };
  }

  // Bare key: truthy check
  return { type: 'key', key: trimmed };
}

/**
 * Evaluates a single when-clause AST node against the context.
 */
export function evaluateNode(
  node: WhenClauseNode,
  context: Record<string, unknown>,
): boolean {
  switch (node.type) {
    case 'and':
      return evaluateNode(node.left, context) && evaluateNode(node.right, context);
    case 'or':
      return evaluateNode(node.left, context) || evaluateNode(node.right, context);
    case 'not':
      return !evaluateNode(node.operand, context);
    case 'equals':
      return String(resolveContextKey(context, node.key)) === node.value;
    case 'notEquals':
      return String(resolveContextKey(context, node.key)) !== node.value;
    case 'key':
      return !!resolveContextKey(context, node.key);
    case 'regex': {
      const val = String(resolveContextKey(context, node.key) ?? '');
      try {
        return new RegExp(node.pattern).test(val);
      } catch {
        return false;
      }
    }
  }
}

/**
 * Serializes a when-clause AST back to a string.
 */
export function serializeWhenClause(ast: WhenClauseNode): string {
  switch (ast.type) {
    case 'and':
      return `${serializeWhenClause(ast.left)} && ${serializeWhenClause(ast.right)}`;
    case 'or':
      return `${serializeWhenClause(ast.left)} || ${serializeWhenClause(ast.right)}`;
    case 'not':
      return `!${serializeWhenClause(ast.operand)}`;
    case 'equals':
      return `${ast.key} == '${ast.value}'`;
    case 'notEquals':
      return `${ast.key} != '${ast.value}'`;
    case 'key':
      return ast.key;
    case 'regex':
      return `${ast.key} =~ /${ast.pattern}/`;
  }
}

// ═══ §6 BUILT-IN COMMANDS — common editor commands with default keybindings ═══

/**
 * Array of ~40 common built-in editor commands.
 */
export const BUILTIN_COMMANDS: CommandData[] = [
  { id: asCommandId('editor.action.undo'), title: 'Undo', category: 'Edit' },
  { id: asCommandId('editor.action.redo'), title: 'Redo', category: 'Edit' },
  { id: asCommandId('editor.action.cut'), title: 'Cut', category: 'Edit' },
  { id: asCommandId('editor.action.copy'), title: 'Copy', category: 'Edit' },
  { id: asCommandId('editor.action.paste'), title: 'Paste', category: 'Edit' },
  { id: asCommandId('editor.action.selectAll'), title: 'Select All', category: 'Edit' },
  { id: asCommandId('editor.action.find'), title: 'Find', category: 'Edit' },
  { id: asCommandId('editor.action.replace'), title: 'Replace', category: 'Edit' },
  { id: asCommandId('editor.action.findInFiles'), title: 'Find in Files', category: 'Search' },
  { id: asCommandId('editor.action.replaceInFiles'), title: 'Replace in Files', category: 'Search' },
  { id: asCommandId('editor.action.goToLine'), title: 'Go to Line', category: 'Navigation' },
  { id: asCommandId('editor.action.goToFile'), title: 'Go to File', category: 'Navigation' },
  { id: asCommandId('editor.action.goToSymbol'), title: 'Go to Symbol', category: 'Navigation' },
  { id: asCommandId('editor.action.goToDefinition'), title: 'Go to Definition', category: 'Navigation' },
  { id: asCommandId('editor.action.goToReferences'), title: 'Go to References', category: 'Navigation' },
  { id: asCommandId('editor.action.goToImplementation'), title: 'Go to Implementation', category: 'Navigation' },
  { id: asCommandId('editor.action.peekDefinition'), title: 'Peek Definition', category: 'Navigation' },
  { id: asCommandId('editor.action.toggleComment'), title: 'Toggle Line Comment', category: 'Edit' },
  { id: asCommandId('editor.action.toggleBlockComment'), title: 'Toggle Block Comment', category: 'Edit' },
  { id: asCommandId('editor.action.formatDocument'), title: 'Format Document', category: 'Edit' },
  { id: asCommandId('editor.action.formatSelection'), title: 'Format Selection', category: 'Edit' },
  { id: asCommandId('editor.action.indentLines'), title: 'Indent Lines', category: 'Edit' },
  { id: asCommandId('editor.action.outdentLines'), title: 'Outdent Lines', category: 'Edit' },
  { id: asCommandId('editor.action.foldAll'), title: 'Fold All', category: 'View' },
  { id: asCommandId('editor.action.unfoldAll'), title: 'Unfold All', category: 'View' },
  { id: asCommandId('editor.action.foldLevel1'), title: 'Fold Level 1', category: 'View' },
  { id: asCommandId('editor.action.foldLevel2'), title: 'Fold Level 2', category: 'View' },
  { id: asCommandId('editor.action.toggleMinimap'), title: 'Toggle Minimap', category: 'View' },
  { id: asCommandId('editor.action.toggleWordWrap'), title: 'Toggle Word Wrap', category: 'View' },
  { id: asCommandId('editor.action.toggleRenderWhitespace'), title: 'Toggle Render Whitespace', category: 'View' },
  { id: asCommandId('editor.action.splitEditor'), title: 'Split Editor', category: 'View' },
  { id: asCommandId('editor.action.splitEditorOrthogonal'), title: 'Split Editor Orthogonal', category: 'View' },
  { id: asCommandId('editor.action.closeEditor'), title: 'Close Editor', category: 'View' },
  { id: asCommandId('editor.action.closeOtherEditors'), title: 'Close Other Editors', category: 'View' },
  { id: asCommandId('editor.action.closeAllEditors'), title: 'Close All Editors', category: 'View' },
  { id: asCommandId('editor.action.newFile'), title: 'New File', category: 'File' },
  { id: asCommandId('editor.action.saveFile'), title: 'Save File', category: 'File' },
  { id: asCommandId('editor.action.saveFileAs'), title: 'Save File As', category: 'File' },
  { id: asCommandId('editor.action.saveAllFiles'), title: 'Save All Files', category: 'File' },
  { id: asCommandId('editor.action.commandPalette'), title: 'Command Palette', category: 'View' },
  { id: asCommandId('editor.action.quickOpen'), title: 'Quick Open', category: 'View' },
  { id: asCommandId('editor.action.toggleSidebar'), title: 'Toggle Sidebar', category: 'View' },
  { id: asCommandId('editor.action.togglePanel'), title: 'Toggle Panel', category: 'View' },
  { id: asCommandId('editor.action.toggleZenMode'), title: 'Toggle Zen Mode', category: 'View' },
  { id: asCommandId('editor.action.toggleFullScreen'), title: 'Toggle Full Screen', category: 'View' },
  { id: asCommandId('editor.action.zoomIn'), title: 'Zoom In', category: 'View' },
  { id: asCommandId('editor.action.zoomOut'), title: 'Zoom Out', category: 'View' },
  { id: asCommandId('editor.action.resetZoom'), title: 'Reset Zoom', category: 'View' },
];

/**
 * Default keybindings for built-in commands.
 */
export const BUILTIN_KEYBINDINGS: KeybindingData[] = [
  { id: asKeybindingId('kb_undo'), key: 'ctrl+z', command: asCommandId('editor.action.undo') },
  { id: asKeybindingId('kb_redo'), key: 'ctrl+shift+z', command: asCommandId('editor.action.redo') },
  { id: asKeybindingId('kb_cut'), key: 'ctrl+x', command: asCommandId('editor.action.cut') },
  { id: asKeybindingId('kb_copy'), key: 'ctrl+c', command: asCommandId('editor.action.copy') },
  { id: asKeybindingId('kb_paste'), key: 'ctrl+v', command: asCommandId('editor.action.paste') },
  { id: asKeybindingId('kb_selectAll'), key: 'ctrl+a', command: asCommandId('editor.action.selectAll') },
  { id: asKeybindingId('kb_find'), key: 'ctrl+f', command: asCommandId('editor.action.find') },
  { id: asKeybindingId('kb_replace'), key: 'ctrl+h', command: asCommandId('editor.action.replace') },
  { id: asKeybindingId('kb_findInFiles'), key: 'ctrl+shift+f', command: asCommandId('editor.action.findInFiles') },
  { id: asKeybindingId('kb_replaceInFiles'), key: 'ctrl+shift+h', command: asCommandId('editor.action.replaceInFiles') },
  { id: asKeybindingId('kb_goToLine'), key: 'ctrl+g', command: asCommandId('editor.action.goToLine') },
  { id: asKeybindingId('kb_goToFile'), key: 'ctrl+p', command: asCommandId('editor.action.goToFile') },
  { id: asKeybindingId('kb_goToSymbol'), key: 'ctrl+shift+o', command: asCommandId('editor.action.goToSymbol') },
  { id: asKeybindingId('kb_goToDefinition'), key: 'f12', command: asCommandId('editor.action.goToDefinition') },
  { id: asKeybindingId('kb_goToReferences'), key: 'shift+f12', command: asCommandId('editor.action.goToReferences') },
  { id: asKeybindingId('kb_goToImplementation'), key: 'ctrl+f12', command: asCommandId('editor.action.goToImplementation') },
  { id: asKeybindingId('kb_peekDefinition'), key: 'alt+f12', command: asCommandId('editor.action.peekDefinition') },
  { id: asKeybindingId('kb_toggleComment'), key: 'ctrl+/', command: asCommandId('editor.action.toggleComment') },
  { id: asKeybindingId('kb_toggleBlockComment'), key: 'ctrl+shift+/', command: asCommandId('editor.action.toggleBlockComment') },
  { id: asKeybindingId('kb_formatDocument'), key: 'ctrl+shift+i', command: asCommandId('editor.action.formatDocument') },
  { id: asKeybindingId('kb_indentLines'), key: 'ctrl+]', command: asCommandId('editor.action.indentLines') },
  { id: asKeybindingId('kb_outdentLines'), key: 'ctrl+[', command: asCommandId('editor.action.outdentLines') },
  { id: asKeybindingId('kb_foldAll'), key: 'ctrl+k ctrl+0', command: asCommandId('editor.action.foldAll'), when: 'editorTextFocus' },
  { id: asKeybindingId('kb_unfoldAll'), key: 'ctrl+k ctrl+j', command: asCommandId('editor.action.unfoldAll'), when: 'editorTextFocus' },
  { id: asKeybindingId('kb_toggleMinimap'), key: 'ctrl+shift+m', command: asCommandId('editor.action.toggleMinimap') },
  { id: asKeybindingId('kb_toggleWordWrap'), key: 'alt+z', command: asCommandId('editor.action.toggleWordWrap') },
  { id: asKeybindingId('kb_splitEditor'), key: 'ctrl+\\', command: asCommandId('editor.action.splitEditor') },
  { id: asKeybindingId('kb_closeEditor'), key: 'ctrl+w', command: asCommandId('editor.action.closeEditor') },
  { id: asKeybindingId('kb_closeAllEditors'), key: 'ctrl+k ctrl+w', command: asCommandId('editor.action.closeAllEditors') },
  { id: asKeybindingId('kb_newFile'), key: 'ctrl+n', command: asCommandId('editor.action.newFile') },
  { id: asKeybindingId('kb_saveFile'), key: 'ctrl+s', command: asCommandId('editor.action.saveFile') },
  { id: asKeybindingId('kb_saveFileAs'), key: 'ctrl+shift+s', command: asCommandId('editor.action.saveFileAs') },
  { id: asKeybindingId('kb_saveAllFiles'), key: 'ctrl+k s', command: asCommandId('editor.action.saveAllFiles') },
  { id: asKeybindingId('kb_commandPalette'), key: 'ctrl+shift+p', command: asCommandId('editor.action.commandPalette') },
  { id: asKeybindingId('kb_quickOpen'), key: 'ctrl+p', command: asCommandId('editor.action.quickOpen') },
  { id: asKeybindingId('kb_toggleSidebar'), key: 'ctrl+b', command: asCommandId('editor.action.toggleSidebar') },
  { id: asKeybindingId('kb_togglePanel'), key: 'ctrl+j', command: asCommandId('editor.action.togglePanel') },
  { id: asKeybindingId('kb_toggleZenMode'), key: 'ctrl+k z', command: asCommandId('editor.action.toggleZenMode') },
  { id: asKeybindingId('kb_zoomIn'), key: 'ctrl+=', command: asCommandId('editor.action.zoomIn') },
  { id: asKeybindingId('kb_zoomOut'), key: 'ctrl+-', command: asCommandId('editor.action.zoomOut') },
  { id: asKeybindingId('kb_resetZoom'), key: 'ctrl+0', command: asCommandId('editor.action.resetZoom') },
];

// ═══ INTERNAL HELPERS ═══

/**
 * Simple fuzzy match: checks if all characters of the pattern appear in order in the text.
 * Returns a score based on how closely the characters are grouped.
 */
function fuzzyMatch(pattern: string, text: string): number {
  let patternIdx = 0;
  let score = 0;
  let lastMatchIdx = -1;

  for (let i = 0; i < text.length && patternIdx < pattern.length; i++) {
    if (text[i] === pattern[patternIdx]) {
      score += 10;
      // Bonus for consecutive matches
      if (lastMatchIdx === i - 1) score += 5;
      // Bonus for matching at word boundaries
      if (i === 0 || text[i - 1] === ' ' || text[i - 1] === '.' || text[i - 1] === '_') {
        score += 8;
      }
      lastMatchIdx = i;
      patternIdx++;
    }
  }

  return patternIdx === pattern.length ? score : 0;
}

/**
 * Splits a string by an operator, respecting parentheses.
 */
function splitByOperator(text: string, operator: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = '';

  for (let i = 0; i < text.length; i++) {
    if (text[i] === '(') depth++;
    if (text[i] === ')') depth--;

    if (depth === 0 && text.substring(i, i + operator.length) === operator) {
      parts.push(current.trim());
      current = '';
      i += operator.length - 1;
    } else {
      current += text[i];
    }
  }

  parts.push(current.trim());
  return parts.filter((p) => p.length > 0);
}

/**
 * Resolves a dotted key path from a context object.
 */
function resolveContextKey(context: Record<string, unknown>, key: string): unknown {
  const parts = key.split('.');
  let current: unknown = context;

  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }

  return current;
}

/**
 * Formats a key name for display (capitalizes common keys).
 */
function formatKeyName(key: string): string {
  const names: Record<string, string> = {
    'escape': 'Esc',
    'enter': 'Enter',
    'return': 'Enter',
    'space': 'Space',
    'backspace': 'Backspace',
    'delete': 'Delete',
    'tab': 'Tab',
    'up': '\u2191',
    'down': '\u2193',
    'left': '\u2190',
    'right': '\u2192',
    'home': 'Home',
    'end': 'End',
    'pageup': 'PgUp',
    'pagedown': 'PgDn',
  };

  const lower = key.toLowerCase();
  return names[lower] ?? key.toUpperCase();
}
